import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { registerUser } from '../utils/api/userAPI';
import { getAllOrganisationData } from '../utils/api/organisationAPI';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { getAllRole } from '../utils/api/roleAPI';

const AddUserLayer = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    roleId: 'c18df413-3598-469f-b0bb-7bc341e0f4ef',
    organizationId: '',
  });
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Promise.all([getAllRole(), getAllOrganisationData({ setIsLoading })])
      .then(([roleData, orgData]) => {
        setRoles(roleData || []);
        setOrganizations(orgData.organizations || []);
      })
      .catch(() => toast.error('Failed to fetch roles or organizations'));
  }, []);


  const validate = () => {
    const { firstName, lastName, email, password, organizationId, phone } = formData;
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Invalid email";
    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^\d{10}$/.test(phone)) newErrors.phone = "Invalid phone";
    if (!password.trim()) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "Minimum 6 characters";
    if (!organizationId) newErrors.organizationId = "Organization is required";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      password: true,
      organizationId: true,
    });

    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const res = await registerUser(formData);
      toast.success(res.message);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        roleId: 'c18df413-3598-469f-b0bb-7bc341e0f4ef',
        organizationId: '',
      });
      setErrors({});
      setTouched({});
    } catch (err) {
      toast.error(typeof err === 'string' ? err : err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const renderInput = (name, type, placeholder, icon, label) => (
    <div className="col-md-6" key={name}>
      <label className="form-label label-style">{label}</label>
      <div style={{ position: 'relative' }}>
        <Icon icon={icon} width="18" height="18" className="icon-style" />
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          onBlur={handleBlur}
          className="form-control input-style"
          placeholder={placeholder}
        />
        {touched[name] && errors[name] && (
          <div className="text-danger">{errors[name]}</div>
        )}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="col-lg-12"
    >
      <div className="card card-style">
        <div className="card-header card-header-style">
          <h5 className="card-title mb-0 title-style">
            <Icon icon="lucide:user-plus" className="me-2" width="22" height="22" />
            Add User Form
          </h5>
        </div>
        <div className="card-body card-body-style">
          <form className="row gy-4" onSubmit={handleSubmit}>
            {renderInput('firstName', 'text', 'Enter first name', 'lucide:user', 'First Name')}
            {renderInput('lastName', 'text', 'Enter last name', 'lucide:user', 'Last Name')}
            {renderInput('email', 'email', 'Enter email', 'lucide:mail', 'Email')}
            {renderInput('phone', 'text', 'Enter phone', 'lucide:phone', 'Phone')}
            {renderInput('password', 'password', 'Enter password', 'lucide:lock', 'Password')}

            <div className="col-md-6">
              <label className="form-label label-style">Organization</label>
              <div className="position-relative">
                <Icon icon="lucide:building" width="18" height="18" className="icon-style" />
                <select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="form-control select-style"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                {touched.organizationId && errors.organizationId && (
                  <div className="text-danger">{errors.organizationId}</div>
                )}
                <Icon icon="lucide:chevron-down" width="18" height="18" className="dropdown-icon-style" />
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label label-style">Roles</label>
              <div className="position-relative">
                <Icon icon="lucide:badge-check" width="18" height="18" className="icon-style" />
                <select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  className="form-control select-style"
                >
                  <option value="">Select Roles</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" width="18" height="18" className="dropdown-icon-style" />
              </div>
            </div>

            <div className="col-12 footer-style">
              <button type="submit" className="submit-btn-style" disabled={isLoading}>
                {isLoading ? <Spinner animation="border" size="sm" /> : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
};

export default AddUserLayer;
